"use client";

import { useState } from "react";
import { ArrowRight, Pause, Play } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Resources() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [activeId, setActiveId] = useState<number | null>(null);
  const projects = [{
    id: 1,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/imaging_suite_setup.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/imaging_suite_setup.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/imaging_suite_setup.png-wvc-sizes",
    title: "Clinical Imaging Workflow",
    client: "University Medical Center",
    episodes: "04 MODULES",
    duration: "1H 15M",
    span: "large",
    offset: false
  }, {
    id: 2,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/patient_positioning_guide.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/patient_positioning_guide.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/patient_positioning_guide.png-wvc-sizes",
    title: "Patient Positioning",
    client: "Regional Hospital",
    episodes: "02 MODULES",
    duration: "0H 45M",
    span: "small",
    offset: true
  }, {
    id: 3,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/support_system_demo.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/support_system_demo.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/support_system_demo.png-wvc-sizes",
    title: "System Integration",
    client: "Diagnostic Clinic",
    episodes: "06 MODULES",
    duration: "2H 10M",
    span: "small",
    offset: false
  }, {
    id: 4,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/safety_protocols_video.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/safety_protocols_video.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/safety_protocols_video.png-wvc-sizes",
    title: "Safety & Compliance",
    client: "Imaging Research Lab",
    episodes: "08 MODULES",
    duration: "3H 20M",
    span: "large",
    offset: true
  }, {
    id: 5,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/maintenance_care_guide.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/maintenance_care_guide.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/maintenance_care_guide.png-wvc-sizes",
    title: "Maintenance & Care",
    client: "NoMoCo Support",
    episodes: "03 MODULES",
    duration: "0H 55M",
    span: "large",
    offset: false
  }, {
    id: 6,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/clinical_case_studies.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/clinical_case_studies.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709983/img/clinical_case_studies.png-wvc-sizes",
    title: "Clinical Case Studies",
    client: "Radiology Partners",
    episodes: "05 MODULES",
    duration: "1H 40M",
    span: "small",
    offset: true
  }];
  const togglePlay = (id: number) => {
    setActiveId(prev => prev === id ? null : id);
  };
  return <section data-section-id="5034" id="imaging-resources" className="bg-background text-foreground py-24 md:py-32 lg:py-48 px-6 md:px-12 lg:px-20 overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-8">
            <p className="font-default text-xs md:text-sm tracking-widest mb-8 md:mb-12 text-accent">
              02 — CLINICAL RESOURCES
            </p>
            <h2 className="font-default font-bold text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight text-primary">
              Professional{" "}
              <span className="italic font-normal text-primary">
                imaging
              </span>{" "}
              support.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:items-end lg:justify-end">
            <p className="text-base md:text-lg font-default text-foreground max-w-sm lg:text-right leading-relaxed">
              A curated library of instructional guides and clinical resources for the NoMoCo system.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className="mt-20 md:mt-28 lg:mt-32 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 lg:gap-10">
          {projects.map((project, i) => {
          const isLarge = project.span === "large";
          const colSpan = isLarge ? "md:col-span-7" : "md:col-span-5";
          const offsetClass = project.offset ? "md:mt-16 lg:mt-20" : "";
          const aspectClass = isLarge ? "aspect-[4/3]" : "aspect-[3/4]";
          const isActive = activeId === project.id;
          return <div key={project.id} data-index={i} className={`group ${colSpan} ${offsetClass} transition-all duration-1000 ease-out`} style={{
            opacity: gridVisible ? 1 : 0,
            transform: gridVisible ? "translateY(0)" : "translateY(40px)",
            transitionDelay: `${i * 120}ms`
          }}>
                <Button onClick={() => togglePlay(project.id)} variant="ghost" className="w-full text-left p-0 h-auto hover:bg-transparent focus-visible:ring-0" aria-label={`Play ${project.title}`}>
                  <div className={`relative w-full ${aspectClass} overflow-hidden rounded-md`}>
                    {}
                    <img src={project.image} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={project.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out grayscale-[60%] group-hover:grayscale-0 scale-105 group-hover:scale-100" />

                    {}
                    <div className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-30" style={{
                  backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
                }} />

                    {}
                    <div className="absolute inset-0 pointer-events-none" style={{
                  background: "linear-gradient(to top, hsl(228, 69%, 40%) 0%, hsla(228, 69%, 40%, 0) 50%)"
                }} />

                    {}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 md:w-16 md:h-16 rounded-md flex items-center justify-center transition-all duration-500 ease-out group-hover:scale-110 backdrop-blur-sm" style={{
                    border: "1.5px solid hsl(215, 27%, 62%)",
                    backgroundColor: "hsla(215, 27%, 62%, 0.3)"
                  }}>
                        {isActive ? <Pause className="w-5 h-5 md:w-6 md:h-6" style={{
                      color: "white"
                    }} strokeWidth={1.5} /> : <Play className="w-5 h-5 md:w-6 md:h-6 ml-0.5" style={{
                      color: "white"
                    }} strokeWidth={1.5} fill="currentColor" />}
                      </div>
                    </div>

                    {}
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/10 overflow-hidden">
                      <div className="h-full transition-all duration-700 ease-out" style={{
                    backgroundColor: "hsl(228, 69%, 40%)",
                    width: isActive ? "100%" : "0%",
                    transform: !isActive ? "translateX(-100%)" : "translateX(0)"
                  }} />
                    </div>
                  </div>

                  {}
                  <div className="mt-5 md:mt-6 text-left">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-default text-[11px] md:text-xs tracking-widest text-accent">
                        {project.episodes}
                      </span>
                      <span className="w-1 h-1 rounded-md bg-accent" />
                      <span className="font-default text-[11px] md:text-xs tracking-widest text-accent">
                        {project.duration}
                      </span>
                    </div>
                    <h3 className="font-default font-bold text-xl md:text-2xl text-primary leading-snug mb-1">
                      {project.title}
                    </h3>
                    <p className="font-default text-[13px] text-foreground tracking-wide">
                      {project.client}
                    </p>
                  </div>

                  {}
                  {isActive && <div className="mt-4 p-4 rounded-md border text-left" style={{
                backgroundColor: "hsl(215, 30%, 96%)",
                borderColor: "hsl(215, 27%, 85%)"
              }}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-2 h-2 rounded-md animate-pulse" style={{
                    backgroundColor: "hsl(228, 69%, 40%)"
                  }} />
                        <span className="font-default text-[10px] tracking-widest text-primary">
                          PLAYING — MODULE 01
                        </span>
                      </div>
                      <Progress value={34} className="h-1 bg-accent" />
                      <div className="flex justify-between mt-2 font-default text-[10px] text-foreground">
                        <span>00:12:48</span>
                        <span>00:37:22</span>
                      </div>
                    </div>}
                </Button>
              </div>;
        })}
        </div>

        {}
        <div className="mt-24 md:mt-32 flex justify-center">
          <Button asChild variant="ghost" className="group font-default text-base md:text-lg text-primary hover:bg-transparent px-0 h-auto py-3">
            <Link to="/MediaPage" className="inline-flex items-center gap-3">
              <span className="relative">
                View Full Library
                <span className="absolute left-0 right-0 -bottom-1 h-px transition-all duration-500 group-hover:opacity-100 opacity-40 bg-primary" />
              </span>
              <ArrowRight className="w-5 h-5 transition-transform duration-500 group-hover:translate-x-2 text-primary" strokeWidth={1.5} />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}